import React, { useEffect, useState } from "react";
import styles from './StudentOverview.module.scss';
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import { Tab, TabPanel, Tabs } from "../../../components/tabs/Tabs";
import ProfilePicture from "../../../components/profilePicture/ProfilePicture";
import Button from "../../../components/button/Button";
import EvaluationCard from "../../../components/evaluationCard/EvaluationCard";
import EvaluationRequestCard from "../../../components/evaluationRequestCard/EvaluationRequestCard";
import axiosClient from "../../../api/axiosClient";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import { calculateTimeDifference, getDay } from "../../../utils/timeUtils";
import LoadMore from "../../../components/loadMore/loadMore";
import { useToasts } from "../../../contexts/ToastContext";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import Modal from "../../../components/modal/Modal";
import BtTable from "../../../components/btTable/BtTable";
import Icon from "../../../components/icon/Icon";
import { format } from "date-fns";
import DropDownButton from "../../../components/dropDownButton/DropDownButton";
import Filter from "../../../components/filter/Filter";
import TextField from "../../../components/textField/TextField";
import MultiSelectDropdown from "../../../components/multiSelectDropdown/MultiSelectDropdown";
import CustomDatePicker from "../../../components/customDatePicker/CustomDatePicker";

const StudentOverview = () => {
  const limit = 20;
  const [isLoading, setIsLoading] = useState(false);
  /* About Tab */
  const [about, setAbout] = useState({});
  /* Pending Tab */
  const [pendingEvaluations, setPendingEvaluations] = useState([]);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  const [isPendingLoading, setIsPendingLoading] = useState(false);
  const [pendingFilter, setPendingFilter] = useState('Pending');
  /* Completed Tab */
  const [completedEvaluations, setCompletedEvaluations] = useState([]);
  const [completedPage, setCompletedPage] = useState(1);
  const [completedCount, setCompletedCount] = useState(0);
  const [completedTotalPages, setCompletedTotalPages] = useState(1);
  const [isCompletedLoading, setIsCompletedLoading] = useState(false);
  const [completedFilter, setCompletedFilter] = useState('Completed');
  /* Requests Tab */
  const [requests, setRequests] = useState([]);
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestsCount, setRequestsCount] = useState(0);
  const [requestsTotalPages, setRequestsTotalPages] = useState(1);
  const [isRequestsLoading, setIsRequestsLoading] = useState(false);
  /* Other */
  const [loadingCard, setLoadingCard] = useState(null);
  const [isBenchmarksOpened, setIsBenchmarksOpened] = useState(false);
  const [benchmarksData, setBenchmarksData] = useState(null);
  const [selectedSpeciality, setSelectedSpeciality] = useState(null);
  const [specialities, setSpecialities] = useState([]);
  const { studentId } = useParams();
  const { showToast } = useToasts();
  const navigate = useNavigate();

  useEffect(() => {
    axiosClient.get(`specialities`)
    .then((response) => {
      if(response.data) {
        setSpecialities(response.data);
      }
    })
    .catch((error) => {
      showToast(error.message, 'error');
    });
  }, [showToast]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await axiosClient.get(`users/student/${studentId}`)
      .then((aboutResponse) => {
        if(aboutResponse.data) {
          setAbout(aboutResponse.data);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
    };

    fetchData();
  }, [studentId, showToast]);

  useEffect(() => {
    const fetchData = async () => {
      setIsPendingLoading(true);
      await axiosClient.get(`evaluations`, {
        params: {
          studentId: studentId,
          status: pendingFilter,
          limit: limit,
          page: pendingPage,
        }
      })
      .then ((response) => {
        if(response.data?.evaluations) {
          if(pendingPage === 1) {
            setPendingEvaluations([]);
          }
          setPendingEvaluations((prevData) => {
            return [...prevData, ...response.data.evaluations.map((evaluation) => {
              const { isExpired, hours, timeDifference } = calculateTimeDifference(evaluation.missedDate);
              return {
                evaluationId: evaluation.id.toString(),
                timeRemaining: isExpired ? evaluation.status : timeDifference,
                timeColor: isExpired || hours < 2 ? 'red' : 'green',
                totalCount: evaluation.total,
                completedCount: evaluation.completed,
                isExpired: evaluation.status === "Expired",
              };
            })];
          });
          setPendingCount(response.data.total)
          setPendingTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsPendingLoading(false);
      });
    }

    fetchData();
  }, [studentId, pendingPage, pendingFilter, showToast]);

  useEffect(() => {
    setPendingPage(1);
    setPendingEvaluations([]);
  }, [pendingFilter]);

  useEffect(() => {
    const fetchData = async () => {
      setIsCompletedLoading(true);
      await axiosClient.get(`evaluations`, {
        params: {
          studentId: studentId,
          status: completedFilter,
          limit: limit,
          page: completedPage,
        }
      })
      .then ((response) => {
        if(response.data?.evaluations) {
          if(completedPage === 1) {
            setCompletedEvaluations([]);
          }
          setCompletedEvaluations((prevData) => {
            return [...prevData, ...response.data.evaluations.map((evaluation) => {
              return {
                evaluationId: evaluation.id.toString(),
                completedOn: format(evaluation.completedDate, 'MM/dd/yyyy'),
                totalCount: evaluation.total,
                completedCount: evaluation.completed,
                isLate: evaluation.status === 'Late Submitted',
              }
            })];
          });
          setCompletedCount(response.data.total)
          setCompletedTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsCompletedLoading(false);
      });
    }

    fetchData();
  }, [studentId, completedPage, completedFilter, showToast]);

  useEffect(() => {
    setCompletedPage(1);
    setCompletedEvaluations([]);
  }, [completedFilter]);

  useEffect(() => {
    const fetchData = async () => {
      setIsRequestsLoading(true);
      await axiosClient.get(`evaluation-requests`, {
        params: {
          studentId: studentId,
          limit: limit,
          page: requestsPage,
        }
      })
      .then ((response) => {
        if(response.data?.evaluationRequests) {
          if(requestsPage === 1) {
            setRequests([]);
          }
          setRequests((prevData) => {
            return [...prevData, ...response.data.evaluationRequests.map((request) => {
              return {
                id: request.id,
                profilePicture: request.student?.profilePicture ?? null,
                firstName: request.student?.firstName ?? '',
                lastName: request.student?.lastName ?? '',
                date: format(request.date, 'MM/dd/yyyy'),
                status: request.status ?? null,
              }
            })];
          });
          setRequestsCount(response.data.total)
          setRequestsTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsRequestsLoading(false);
      });
    }

    fetchData();
  }, [studentId, requestsPage, showToast]);

  const handleUpdateStatus = async (id, status) => {
    setLoadingCard(id);
    await axiosClient.patch(`evaluation-requests/status/${id}`, {
      status,
      comment: 'Declined'
    })
    .then(() => {
      const updatedRequests = requests.map(item => 
        item.id === id ? { ...item, status: status } : item
      );
      setRequests(updatedRequests);
      showToast(`You have ${status.toLowerCase()} the evaluation request`, status === 'Accepted' ? 'info' : 'error');
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setLoadingCard(null);
    });
  };

  const pendingLoadMore = async () => {
    const page = pendingPage + 1;
    setPendingPage(page);
  };

  const completedLoadMore = async () => {
    const page = completedPage + 1;
    setCompletedPage(page);
  };

  const requestsLoadMore = async () => {
    const page = requestsPage + 1;
    setRequestsPage(page);
  };

  const handleStartEvaluation = async () => {
    setIsBenchmarksOpened(true);
    if(benchmarksData === null) {
      await axiosClient.get(`benchmarks`, {
        params: {
          studentId: studentId,
        }
      })
      .then((response) => {
        setBenchmarksData(response.data);
      })
      .catch((error) => {
        showToast(error.message, 'error');
      });
    }
  }

  const handleAcknowledge = async () => {
    setIsLoading(true);
    await axiosClient.post(`evaluations`, {
      studentId,
      specialityId: selectedSpeciality
    })
    .then((response) => {
      if(response.data) {
        navigate(`/student-overview/evaluation/${response.data.id}`);
      } else {
        showToast('An error occurred. Please try again later ', 'error');
      }
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  let previousDate = null;

  const renderEvaluationAction = () => {
    if (specialities && specialities.length > 1) {
      return (
        <DropDownButton
          label="Start Evaluation"
          items={specialities.map(item => ({
            label: item.name,
            onClick: () => {
              setSelectedSpeciality(item.id);
              handleStartEvaluation();
            }
          }))}
          position="top"
          size="large"
        />
      );
    } else {
      return (
        <Button
          label="Start Evaluation"
          onClick={() => {
            if (specialities) {
              setSelectedSpeciality(specialities[0]?.id);
            }
            handleStartEvaluation();
          }}
        />
      );
    }
  };

  return (
    <div className={styles.container}>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: `${about.firstName ?? ''} ${about.lastName ?? ''}` }
        ]} />
      </div>
      <Tabs>
        <Tab>
          <Icon name="user" />
          <span>About</span>
        </Tab>
        <Tab>
          <Icon name="clock" />
          <span>Pending</span>
          {pendingCount ? <span className={styles.count}>{pendingCount}</span> : ''}
        </Tab>
        <Tab>
          <Icon name="check-circle" />
          <span>Completed</span>
          {completedCount ? <span className={styles.count}>{completedCount}</span> : ''}
        </Tab>
        <Tab>
          <Icon name="flag" />
          <span>Requests</span>
          {requestsCount ? <span className={styles.count}>{requestsCount}</span> : ''}
        </Tab>
        
        <TabPanel>
          <div className={styles.scrollContainer}>
            <div className={styles.studentHeader}>
              <ProfilePicture
                profilePicture={ about.profilePicture ?? null}
                firstName={ about.firstName ?? ''}
                lastName={ about.lastName ?? ''}
                size="medium"
              />
              <span>{ about.firstName ?? ''} { about.lastName ?? ''}</span>
            </div>
            <div className={styles.studentDetails}>
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>General Details</h2>
                <div className={styles.row}>
                  <div className={styles.column}>
                    <TextField label="Email" value={about.email ?? ''} disabled onChange={() => {}} />
                  </div>
                  <div className={styles.column}>
                    <TextField label="Phone" value={about.mobileNumber ? `${about.mobileNumber.slice(0, 3)}-${about.mobileNumber.slice(3, 6)}-${about.mobileNumber.slice(6, 10)}` : ''} disabled onChange={() => {}} />
                  </div>
                </div>
                <MultiSelectDropdown
                  label="Clinical Sites"
                  options={(about.clinicalSites ?? []).map(s => ({ value: s.id, label: s.name }))}
                  selected={(about.clinicalSites ?? []).map(s => s.id)}
                  onChange={() => {}}
                  disabled
                />
                <MultiSelectDropdown
                  label="Specialities"
                  options={(about.specialities ?? []).map(s => ({ value: s.id, label: s.name }))}
                  selected={(about.specialities ?? []).map(s => s.id)}
                  onChange={() => {}}
                  disabled
                />
              </div>
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Program Details</h2>
                <div className={styles.row}>
                  <div className={styles.column}>
                    <TextField label="Program Name" value={about.program?.title ?? ''} disabled onChange={() => {}} />
                  </div>
                  <div className={styles.column}>
                    <TextField label="Status" value={about.program?.status === true ? 'Active' : about.program?.status === false ? 'Inactive' : ''} disabled onChange={() => {}} />
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.column25}>
                    <CustomDatePicker label="Start Date" date={about.program?.startDate ? new Date(about.program.startDate) : null} onChange={() => {}} showIcon disabled />
                  </div>
                  <div className={styles.column25}>
                    <CustomDatePicker label="End Date" date={about.program?.endDate ? new Date(about.program.endDate) : null} onChange={() => {}} showIcon disabled />
                  </div>
                  <div className={styles.column50}>
                    <TextField label="Current Year" value={about.program?.currentYear?.year ?? ''} disabled onChange={() => {}} />
                  </div>
                </div>
              </div>
              {(about.program?.programYears ?? []).length > 0 && (
                <div className={styles.section}>
                  <h2 className={styles.sectionTitle}>Program Years</h2>
                  <div className={styles.yearsGrid}>
                    {about.program.programYears.map((yearGroup) => (
                      <div key={yearGroup.year.id} className={styles.yearItem}>
                        <div className={styles.yearTitleRow}>
                          <h3 className={styles.yearTitleSmall}>{yearGroup.year.year}</h3>
                        </div>
                        <div className={styles.yearContent}>
                          <div className={styles.dateRow}>
                            <CustomDatePicker
                              label="Start Date"
                              date={yearGroup.rotationStartDate ? new Date(yearGroup.rotationStartDate) : null}
                              onChange={() => {}}
                              showIcon
                              disabled
                            />
                            <CustomDatePicker
                              label="End Date"
                              date={yearGroup.rotationEndDate ? new Date(yearGroup.rotationEndDate) : null}
                              onChange={() => {}}
                              showIcon
                              disabled
                            />
                          </div>
                          <div className={styles.inputContainer}>
                            <span className={styles.inputLabel}>Rotation Length</span>
                            <div className={styles.displayBox}>
                              {yearGroup.rotationLength ?? 0} {(yearGroup.rotationLength ?? 0) === 1 ? 'Week' : 'Weeks'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={styles.studentActions}>
            { renderEvaluationAction() }
          </div>
        </TabPanel>
        
        <TabPanel>
          <div className={styles.evaluations}>
            <div className={styles.filters}>
              <Filter
                filters={[
                  { label: 'All', value: 'Pending' },
                  { label: 'Missed', value: 'Missed' },
                ]}
                selected={pendingFilter}
                onChange={setPendingFilter}
              />
            </div>
            { pendingEvaluations.length > 0 ? <div className={styles.cardList}>
              {pendingEvaluations.map((item) => {
                return (
                  <div className={styles.cardItem} key={`pending-${item.evaluationId}`}>
                    <EvaluationCard
                      data={[
                        { label: 'Evaluation ID', text: item.evaluationId },
                        { label: 'Time Remaining', text: item.timeRemaining, color: item.timeColor },
                      ]}
                      count={{
                        total: item.totalCount,
                        completed: item.completedCount
                      }}
                      onClick={() => navigate(`/student-overview/evaluation/${item.evaluationId}`)}
                      disabled={item.isExpired}
                    />
                  </div>
                );
              })}
              {(isPendingLoading || pendingPage < pendingTotalPages) 
                && <LoadMore isLoading={isPendingLoading} onClick={pendingLoadMore} />}
            </div> : !isPendingLoading ? <div className={styles.emptyEvaluations}>
              <p>No pending evaluations in the selected period</p>
            </div> : <LoadingSpinner size={33} />}
          </div>
        </TabPanel>
        
        <TabPanel>
          <div className={styles.evaluations}>
            <div className={styles.filters}>
              <Filter
                filters={[
                  { label: 'All', value: 'Completed' },
                  { label: 'Late', value: 'Late' },
                ]}
                selected={completedFilter}
                onChange={setCompletedFilter}
              />
            </div>
            { completedEvaluations.length > 0 ? <div className={styles.cardList}>
              {completedEvaluations.map((item) => {
                return (
                  <div className={styles.cardItem} key={`completed-${item.evaluationId}`}>
                    <EvaluationCard
                      data={[
                        { label: 'Evaluation ID', text: item.evaluationId },
                        { label: 'Submitted Date', text: item.completedOn, tag: item.isLate ? 'Late' : undefined },
                      ]}
                      count={{
                        total: item.totalCount,
                        completed: item.completedCount
                      }}
                      onClick={() => navigate(`/student-overview/evaluation/${item.evaluationId}/summary`)}
                    />
                  </div>
                );
              })}
              {(isCompletedLoading || completedPage < completedTotalPages) 
                && <LoadMore isLoading={isCompletedLoading} onClick={completedLoadMore} />}
            </div> : !isCompletedLoading ? <div className={styles.emptyEvaluations}>
              <p>No completed evaluations in the selected period</p>
            </div> : <LoadingSpinner size={33} />}
          </div>
        </TabPanel>
        
        <TabPanel>
          <div className={styles.scrollContainer}>
            <div className={styles.requests}>
              { requests.length > 0 ? <div className={styles.requestList}>
                {requests.map((request, index) => {
                  const shouldShowDate = request.date !== previousDate;
                  previousDate = request.date;

                  return (
                    <div key={`request-${index}`}>
                      {shouldShowDate && <div className={styles.date}>
                        <span>{getDay(request.date)}</span>
                      </div>}
                      <EvaluationRequestCard
                        {...request}
                        onActionClick={(status) => {
                          handleUpdateStatus(request.id, status);
                        }}
                        isLoading={loadingCard === request.id}
                      />
                    </div>
                  );
                })}
                {(isRequestsLoading || requestsPage < requestsTotalPages) 
                  && <LoadMore isLoading={isRequestsLoading} onClick={requestsLoadMore} />}
              </div> : !isRequestsLoading ? <div className={styles.emptyEvaluations}>
                <p>No requests in the selected period</p>
              </div> : <LoadingSpinner size={33} />}
            </div>
          </div>
          <div className={styles.studentActions}>
            { renderEvaluationAction() }
          </div>
        </TabPanel>
      </Tabs>
      {isBenchmarksOpened && <Modal>
        <BtTable
          data={benchmarksData}
          onCancel={() => setIsBenchmarksOpened(false)}
          onAcknowledge={() => {
            setIsBenchmarksOpened(false);
            handleAcknowledge();
          }}
        />
      </Modal>}
    </div>
  );
};

export default StudentOverview;